import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import { Observable, of, switchMap, tap } from 'rxjs';

const DEBUG = false;

// URLs that drill down into another screen in an overlay - e.g. dashboard > staff records, etc.
// Don't want to refresh the page when the overlay is closed.
const whiteListUrls = ['dashboards', 'items-matrix'];

interface StoreInterface {
	checkUnsavedChanges(opts?: { forceRootStore: boolean }): Observable<boolean>;
	niceName: string;
	forceClear(): void;
}

export const storeAwareCanDeactivate = (
	component: {
		store: StoreInterface;
	},
	currentRoute: ActivatedRouteSnapshot,
	currentState: RouterStateSnapshot,
	nextState: RouterStateSnapshot,
) => {
	const appState = inject(AppStateService);
	const router = inject(Router);

	return appState.isPaintingSecurity$.pipe(
		switchMap(() => {
			if (!component || !component.store || typeof component.store.checkUnsavedChanges !== 'function') {
				return of(true);
			}

			const name = component.store.niceName;

			const currStartUrl = currentState.url.split('/')[1];
			const nextStartUrl = nextState.url.split('/')[1];

			const isBareNextUrl = nextStartUrl === nextState.url.substring(1);

			// Determine whether routing to a completely different page (e.g. /users to /groups)
			// If so, force using the root store for checking changes.

			const changingScreens = nextState.url === `/${nextStartUrl}` && currStartUrl !== nextStartUrl;
			const forceRootStore = currStartUrl !== nextStartUrl || changingScreens; // ||

			if (DEBUG) {
				console.log(
					`%c running ${name}' checkUnsavedChanges. forceRootStore: ${forceRootStore}`,
					'background-color: yellow; font-size: 1rem;',
				);
			}

			return component.store.checkUnsavedChanges({ forceRootStore }).pipe(
				tap((result) => {
					if (DEBUG) {
						console.log(
							`%c ... result for ${name} = ${result}`,
							'background-color: yellow; font-size: 1rem;',
						);
					}

					if (forceRootStore && result && !changingScreens) {
						component.store.forceClear();
					} else if (
						currStartUrl === nextStartUrl &&
						isBareNextUrl &&
						result &&
						!whiteListUrls.includes(nextStartUrl)
					) {
						// Check whether we should do the re-route below.
						// This is to handle pages that change the URL to an automatically selected ID - e.g. in My Record
						// which loads the current user's record.
						const currentNavigation = router.getCurrentNavigation();
						const state =
							currentNavigation?.previousNavigation?.extras?.state ?? currentNavigation?.extras?.state;
						if (state?.['preventDeactivateReroute']) {
							return;
						}

						// OK to navigate, and navigating to the same page without a selection (no ID in the URL),
						// so navigate home, then back to force a reload/reset
						setTimeout(() => {
							router
								.navigateByUrl('/', {
									skipLocationChange: true,
								})
								.then((ok) => {
									if (!ok) {
										return;
									}

									router.navigateByUrl(nextState.url);
								});
						});
					}
				}),
			);
		}),
	);
};
